import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { CUSTOM_FORMATION, positionAt } from "@legacy-manager/engine";

import {
  EscalacaoNoCampo,
  faixaDeCondicao,
  faixasDaEscalacao,
  nomeCurto,
  pontoDaTela,
  pontosDaFormacao,
  type PontoNoCampo,
} from "./EscalacaoNoCampo";
import type { ClubView, LineupInput, OpponentView, PlayerRow, TacticInput } from "../game/types";
import { Crest, FormChips, MoraleFace, strengthColor } from "../ui/atoms";
import { AvatarDoJogador } from "../ui/avatar/Avatar";
import { rotuloDePosicao, useT, type Chave, type Traduzir } from "../i18n";
import "./tatica.css";

/**
 * Tática e escalação, num campo que se mexe como jogo.
 *
 * **Tocar pega, tocar de novo troca.** Um jogador tocado sobe e brilha, os
 * lugares para onde ele pode ir acendem e os outros apagam. Tocar num deles
 * troca os dois. No computador dá também para arrastar e soltar, e os dois
 * gestos terminam no mesmo `trocar`.
 *
 * **Nenhum lugar anda.** O banco tem sempre nove lugares e os de fora têm os
 * deles, com `null` onde não há ninguém. Antes, tirar alguém do banco encolhia
 * a lista e empurrava tudo o que vinha embaixo, rótulo incluído; agora fica um
 * lugar vazio pontilhado e o resto não se mexe.
 *
 * **A postura mora no gramado.** Formação, marcação e ritmo no alto, estilo na
 * régua da lateral. O bloco do time sobe, desce e compacta junto, e é o
 * desenho que diz o que o botão mudou, sem frase de ajuda.
 *
 * **A ficha do jogador pego vai para o cabeçalho da escalação**, no lugar do
 * título, e não abre por cima do campo nem toma o rodapé, que continua com o
 * próximo jogo e o botão da rodada. Capitão e cobradores se marcam ali.
 *
 * Cada mudança completa entra sozinha na fila, e a escalação vai a campo
 * quando a rodada roda, igual ao que vai acontecer nas ligas online. Antes era
 * preciso confirmar: quem trocava a formação e ia jogar sem apertar o botão
 * perdia a troca sem aviso, e o time voltava para a formação de antes.
 */

type Lista = "t" | "b" | "f";
interface Lugar {
  lista: Lista;
  i: number;
}
interface Elenco {
  t: Array<string | null>;
  b: Array<string | null>;
  f: Array<string | null>;
}

const VAGAS_NO_BANCO = 9;
/** Folga nos de fora, para dispensar alguém do banco sem trocar com ninguém. */
const FOLGA_DE_FORA = 2;

const ESTILOS: Array<[TacticInput["style"], Chave]> = [
  ["ofensivo", "tatica.estiloOfensivo"],
  ["equilibrado", "tatica.estiloEquilibrado"],
  ["defensivo", "tatica.estiloDefensivo"],
];
const MARCACOES: Array<[TacticInput["marking"], Chave]> = [
  ["leve", "tatica.marcacaoLeve"],
  ["normal", "tatica.marcacaoNormal"],
  ["forte", "tatica.marcacaoForte"],
];
const RITMOS: Array<[TacticInput["tempo"], Chave]> = [
  ["baixo", "tatica.ritmoBaixo"],
  ["normal", "tatica.ritmoNormal"],
  ["alto", "tatica.ritmoAlto"],
];

type Papel = "capitao" | "penalty" | "freeKick" | "corner";
const PAPEIS: Array<{ papel: Papel; letra: string; rotulo: Chave; classe: "c" | "b" }> = [
  { papel: "capitao", letra: "C", rotulo: "tatica.capitao", classe: "c" },
  { papel: "penalty", letra: "P", rotulo: "tatica.penaltis", classe: "b" },
  { papel: "freeKick", letra: "F", rotulo: "tatica.faltas", classe: "b" },
  { papel: "corner", letra: "E", rotulo: "tatica.escanteios", classe: "b" },
];

const indisponivel = (p: PlayerRow): boolean => p.injuryRounds > 0 || p.suspensionRounds > 0;
const pendurado = (p: PlayerRow): boolean => p.yellow % 3 === 2;
const cansado = (p: PlayerRow): boolean => p.condition < 75 && p.injuryRounds === 0;

/**
 * O que define a escalação que vai a campo, numa linha só.
 *
 * Titulares na ordem das vagas, reservas sem os lugares vazios (a fila não
 * guarda buraco) e a tática campo a campo, em ordem fixa.
 */
function assinatura(titulares: Array<string | null>, reservas: Array<string | null>, tatica: TacticInput): string {
  return [
    titulares.join(","),
    reservas.filter((id) => id !== null).join(","),
    tatica.formation,
    (tatica.custom ?? []).map((v) => `${v.pos}@${v.x.toFixed(3)},${v.y.toFixed(3)}`).join(";"),
    tatica.style,
    tatica.marking,
    tatica.tempo,
    tatica.takers.penalty,
    tatica.takers.freeKick,
    tatica.takers.corner,
    tatica.captainId,
  ].join("|");
}

/** As três listas de tamanho fixo, a partir do que a view trouxe. */
function montarElenco(view: ClubView, vagas: number): Elenco {
  const t: Array<string | null> = Array.from({ length: vagas }, (_, i) => view.lineup.starters[i] ?? null);
  const b: Array<string | null> = view.lineup.bench.slice(0, VAGAS_NO_BANCO);
  while (b.length < VAGAS_NO_BANCO) b.push(null);
  const usados = new Set([...t, ...b].filter((id): id is string => id !== null));
  const f: Array<string | null> = view.squad.filter((p) => !usados.has(p.id)).map((p) => p.id);
  for (let k = 0; k < FOLGA_DE_FORA; k += 1) f.push(null);
  return { t, b, f };
}

/**
 * A força de cada setor, lida do desenho da formação.
 *
 * Quem é defesa, meio ou ataque é a linha em que a vaga cai, e não a sigla da
 * posição: num 4-4-2 o `PD` é meia. O goleiro entra na defesa.
 */
function setores(formacao: string, slots: string[], nota: (indice: number) => number): { ata: number; mei: number; def: number } {
  const linhas = faixasDaEscalacao(formacao, slots);
  const goleiro = linhas.filter((linha) => linha.vagas.some((vaga) => vaga.pos === "GOL"));
  const deLinha = linhas.filter((linha) => !goleiro.includes(linha)).sort((a, b) => b.v - a.v);
  const media = (indices: number[]): number =>
    indices.length === 0 ? 0 : Math.round(indices.reduce((soma, i) => soma + nota(i), 0) / indices.length);
  const indicesDe = (grupo: typeof linhas): number[] => grupo.flatMap((linha) => linha.vagas.map((vaga) => vaga.indice));
  const defesa = deLinha.slice(0, 1);
  const ataque = deLinha.length > 1 ? deLinha.slice(-1) : [];
  const meio = deLinha.slice(1, deLinha.length > 1 ? -1 : undefined);
  return {
    def: media(indicesDe([...goleiro, ...defesa])),
    mei: media(indicesDe(meio)),
    ata: media(indicesDe(ataque)),
  };
}

export function TacticsPanel({
  view,
  busy,
  onConfirm,
  onAuto,
}: {
  view: ClubView;
  busy: boolean;
  onConfirm: (lineup: LineupInput, tactic: TacticInput) => void;
  /** Escalação automática para a tática que está na tela, formação incluída. */
  onAuto: (tactic: TacticInput) => void;
}) {
  const t = useT();
  const [tactic, setTactic] = useState<TacticInput>(view.tactic);
  const personalizada = tactic.formation === CUSTOM_FORMATION && tactic.custom?.length === 11 ? tactic.custom : null;
  const formation = useMemo(
    () => view.formations.find((entry) => entry.name === tactic.formation) ?? view.formations[0],
    [view.formations, tactic.formation],
  );
  const slots = personalizada ? personalizada.map((vaga) => vaga.pos) : (formation?.slots ?? []);
  const [elenco, setElenco] = useState<Elenco>(() => montarElenco(view, slots.length || 11));
  const [pego, setPego] = useState<Lugar | null>(null);
  const [recem, setRecem] = useState<string[]>([]);
  const [negado, setNegado] = useState<string | null>(null);
  const [aba, setAba] = useState<"banco" | "adversario">("banco");

  // O que a tela mandou por último para a fila.
  const enviado = useRef<string | null>(null);

  // Quando a rodada roda ou a escalação automática entra, recomeça do que veio.
  // A volta do que a própria tela mandou não conta: recomeçar dali juntaria o
  // banco e mexeria nos lugares de fora a cada mudança.
  useEffect(() => {
    const vagas = view.formations.find((entry) => entry.name === view.tactic.formation)?.slots.length ?? 11;
    const veio = montarElenco(view, vagas);
    if (enviado.current !== null && assinatura(veio.t, veio.b, view.tactic) === enviado.current) return;
    setTactic(view.tactic);
    setElenco(veio);
    setPego(null);
  }, [view.lineup, view.tactic]);

  const byId = useMemo(() => new Map(view.squad.map((player) => [player.id, player])), [view.squad]);

  const ratingOf = (playerId: string, pos: string): number =>
    view.fitByPosition[pos]?.find((entry) => entry.id === playerId)?.rating ?? 0;
  const notaNaVaga = (indice: number): number => {
    const id = elenco.t[indice];
    return id ? ratingOf(id, slots[indice] ?? "MC") : 0;
  };
  const titulares = elenco.t.filter((id): id is string => id !== null);
  const forca = titulares.length === 0 ? 0 : Math.round(elenco.t.reduce((soma, _, i) => soma + notaNaVaga(i), 0) / slots.length);
  const meus = setores(tactic.formation, slots, notaNaVaga);

  const idEm = (lugar: Lugar): string | null => elenco[lugar.lista][lugar.i] ?? null;
  const chave = (lugar: Lugar): string => `${lugar.lista}:${String(lugar.i)}`;

  /**
   * Como ficam as três listas depois de trocar dois lugares, ou nulo se não pode.
   *
   * Lesionado e suspenso não entram em campo nem ficam no banco. Quando uma
   * troca o mandaria para o banco, ele vai para fora e o lugar do banco fica
   * vazio: é o que permite tirar o titular machucado pondo um reserva, que
   * antes só dava para fazer com quem estava fora dos relacionados.
   */
  function depoisDaTroca(de: Lugar, para: Lugar): Elenco | null {
    if (de.lista === para.lista && de.i === para.i) return null;
    const a = idEm(de);
    const b = idEm(para);
    if (!a) return null;
    const proximo: Elenco = { t: [...elenco.t], b: [...elenco.b], f: [...elenco.f] };
    proximo[de.lista][de.i] = b;
    proximo[para.lista][para.i] = a;
    const mudados: Array<[string | null, Lugar]> = [
      [a, para],
      [b, de],
    ];
    for (const [id, lugar] of mudados) {
      const jogador = id ? byId.get(id) : undefined;
      if (!id || !jogador || !indisponivel(jogador) || lugar.lista === "f") continue;
      // Trocar de vaga dentro do campo não põe ninguém em campo.
      if (lugar.lista === "t") {
        if (de.lista === "t" && para.lista === "t") continue;
        return null;
      }
      proximo.b[lugar.i] = null;
      const vazio = proximo.f.indexOf(null);
      if (vazio >= 0) proximo.f[vazio] = id;
      else proximo.f.push(id);
    }
    // O campo nunca fica com buraco: um titular só sai trocando com alguém.
    if (proximo.t.some((id) => id === null)) return null;
    return proximo;
  }

  /** Para onde o jogador pego pode ir. */
  function podeIr(destino: Lugar): boolean {
    return pego !== null && depoisDaTroca(pego, destino) !== null;
  }

  function classeDe(lugar: Lugar): string {
    const partes: string[] = [];
    if (pego && pego.lista === lugar.lista && pego.i === lugar.i) partes.push("pego");
    else if (pego) partes.push(podeIr(lugar) ? "alvo" : "apagado");
    if (recem.includes(chave(lugar))) partes.push("chegou");
    if (negado === chave(lugar)) partes.push("nao");
    return partes.join(" ");
  }

  function trocar(de: Lugar, para: Lugar): void {
    const a = idEm(de);
    const b = idEm(para);
    const proximo = depoisDaTroca(de, para);
    if (!proximo) return;
    setElenco(proximo);

    // Quem sai do campo deixa braçadeira e cobranças: a braçadeira passa para
    // quem entrou no lugar dele, as cobranças voltam para o automático.
    const saiu = de.lista === "t" && para.lista !== "t" ? a : para.lista === "t" && de.lista !== "t" ? b : null;
    const entrou = saiu === a ? b : a;
    if (saiu) {
      setTactic((antes) => ({
        ...antes,
        captainId: antes.captainId === saiu ? entrou : antes.captainId,
        takers: {
          penalty: antes.takers.penalty === saiu ? null : antes.takers.penalty,
          freeKick: antes.takers.freeKick === saiu ? null : antes.takers.freeKick,
          corner: antes.takers.corner === saiu ? null : antes.takers.corner,
        },
      }));
    }
    setRecem([chave(de), chave(para)]);
    setPego(null);
  }

  useEffect(() => {
    if (recem.length === 0) return;
    const relogio = window.setTimeout(() => setRecem([]), 420);
    return () => window.clearTimeout(relogio);
  }, [recem]);

  useEffect(() => {
    if (!negado) return;
    const relogio = window.setTimeout(() => setNegado(null), 300);
    return () => window.clearTimeout(relogio);
  }, [negado]);

  function tocar(lugar: Lugar): void {
    if (arrastouAgora.current) return;
    if (!pego) {
      if (idEm(lugar)) {
        setPego(lugar);
        setAba("banco");
      }
      return;
    }
    if (pego.lista === lugar.lista && pego.i === lugar.i) return setPego(null);
    if (podeIr(lugar)) return trocar(pego, lugar);
    setNegado(chave(lugar));
  }

  /*
   * Arrastar e soltar.
   *
   * Os ouvintes moram na janela porque o dedo sai de cima do botão no meio do
   * gesto. O estado mais recente chega por ref: os ouvintes são registrados uma
   * vez só, e sem a ref eles leriam o elenco de quando foram criados.
   */
  const raiz = useRef<HTMLDivElement>(null);
  const arrastouAgora = useRef(false);
  const atual = useRef({ elenco, podeIr, trocar, setPego, mover, slots, tactic });
  atual.current = { elenco, podeIr, trocar, setPego, mover, slots, tactic };

  /** O ponto da grama debaixo do dedo, ou nulo fora do campo. */
  const pontoNaGrama = (x: number, y: number): PontoNoCampo | null => {
    const campo = document.elementFromPoint(x, y)?.closest<HTMLElement>(".campo");
    if (!campo || !raiz.current?.contains(campo)) return null;
    const { tactic: tatica } = atual.current;
    return pontoDaTela(campo.getBoundingClientRect(), x, y, tatica.style, tatica.marking);
  };

  useEffect(() => {
    let gesto: { x: number; y: number; lugar: Lugar; ativo: boolean; fantasma: HTMLElement | null } | null = null;
    const lugarDo = (el: Element | null): Lugar | null => {
      const alvo = el?.closest<HTMLElement>("[data-lista]");
      if (!alvo || !raiz.current?.contains(alvo)) return null;
      return { lista: alvo.dataset["lista"] as Lista, i: Number(alvo.dataset["i"]) };
    };
    const limparSobre = (): void => {
      raiz.current?.querySelectorAll(".sobre").forEach((el) => el.classList.remove("sobre"));
    };

    const aoDescer = (e: PointerEvent): void => {
      if (e.button !== 0) return;
      const lugar = lugarDo(e.target as Element);
      if (!lugar) return;
      gesto = { x: e.clientX, y: e.clientY, lugar, ativo: false, fantasma: null };
    };
    const aoMover = (e: PointerEvent): void => {
      if (!gesto) return;
      if (!gesto.ativo) {
        if (Math.hypot(e.clientX - gesto.x, e.clientY - gesto.y) < 8) return;
        if (!atual.current.elenco[gesto.lugar.lista][gesto.lugar.i]) return;
        gesto.ativo = true;
        atual.current.setPego(gesto.lugar);
        const origem = raiz.current?.querySelector(
          `[data-lista="${gesto.lugar.lista}"][data-i="${String(gesto.lugar.i)}"] .campo-foto, [data-lista="${gesto.lugar.lista}"][data-i="${String(gesto.lugar.i)}"] .ficha-foto`,
        );
        if (origem) {
          const fantasma = origem.cloneNode(true) as HTMLElement;
          fantasma.classList.add("tatica-fantasma");
          document.body.appendChild(fantasma);
          gesto.fantasma = fantasma;
        }
      }
      if (gesto.fantasma) {
        gesto.fantasma.style.left = `${String(e.clientX)}px`;
        gesto.fantasma.style.top = `${String(e.clientY)}px`;
      }
      limparSobre();
      const embaixo = document.elementFromPoint(e.clientX, e.clientY)?.closest(".alvo");
      if (embaixo && raiz.current?.contains(embaixo)) embaixo.classList.add("sobre");
      // Titular de linha em cima da grama: a posição que ele vai ganhar.
      if (gesto.fantasma) {
        const doCampo = gesto.lugar.lista === "t" && atual.current.slots[gesto.lugar.i] !== "GOL";
        const ponto = doCampo && !embaixo ? pontoNaGrama(e.clientX, e.clientY) : null;
        if (ponto) gesto.fantasma.dataset["pos"] = rotuloDePosicao(positionAt(ponto.x, ponto.y));
        else delete gesto.fantasma.dataset["pos"];
      }
    };
    const aoSubir = (e: PointerEvent): void => {
      const g = gesto;
      gesto = null;
      if (!g?.ativo) return;
      g.fantasma?.remove();
      limparSobre();
      // O clique que o navegador manda depois do arrasto não é um toque.
      arrastouAgora.current = true;
      window.setTimeout(() => {
        arrastouAgora.current = false;
      }, 0);
      const destino = lugarDo(document.elementFromPoint(e.clientX, e.clientY));
      if (destino && atual.current.podeIr(destino)) return atual.current.trocar(g.lugar, destino);
      // Solto na grama, o titular muda de lugar e a formação vira personalizada.
      const ponto = !destino && g.lugar.lista === "t" ? pontoNaGrama(e.clientX, e.clientY) : null;
      if (ponto) atual.current.mover(g.lugar.i, ponto);
      else atual.current.setPego(null);
    };
    const aoCancelar = (): void => {
      gesto?.fantasma?.remove();
      gesto = null;
      limparSobre();
    };
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === "Escape") atual.current.setPego(null);
    };

    const alvo = raiz.current;
    alvo?.addEventListener("pointerdown", aoDescer);
    window.addEventListener("pointermove", aoMover);
    window.addEventListener("pointerup", aoSubir);
    window.addEventListener("pointercancel", aoCancelar);
    window.addEventListener("keydown", aoTeclar);
    return () => {
      alvo?.removeEventListener("pointerdown", aoDescer);
      window.removeEventListener("pointermove", aoMover);
      window.removeEventListener("pointerup", aoSubir);
      window.removeEventListener("pointercancel", aoCancelar);
      window.removeEventListener("keydown", aoTeclar);
    };
  }, []);

  /**
   * Põe o titular da vaga no ponto da grama onde ele foi solto.
   *
   * A formação vira personalizada, e os outros dez ficam onde estavam: da
   * formação da lista, cada um no ponto em que ela o desenhava. A posição de
   * quem mudou sai do ponto. O goleiro não sai do gol.
   */
  function mover(indice: number, ponto: PontoNoCampo): void {
    if (slots[indice] === "GOL") return setPego(null);
    const base =
      personalizada ??
      pontosDaFormacao(formation?.name ?? "", slots).map((p, i) => ({ pos: slots[i] ?? "MC", x: p.x, y: p.y }));
    const custom = base.map((vaga, i) =>
      i === indice ? { pos: positionAt(ponto.x, ponto.y), x: ponto.x, y: ponto.y } : { ...vaga },
    );
    setTactic({ ...tactic, formation: CUSTOM_FORMATION, custom });
    setRecem([chave({ lista: "t", i: indice })]);
    setPego(null);
  }

  function changeFormation(name: string): void {
    const next = view.formations.find((entry) => entry.name === name);
    if (!next) return;
    // Remonta a escalação para a formação nova, escolhendo o melhor disponível
    // para cada vaga em vez de manter jogadores em posições que sumiram.
    const taken = new Set<string>();
    const rebuilt = next.slots.map((pos) => {
      const best = view.fitByPosition[pos]?.find((entry) => entry.available && !taken.has(entry.id));
      if (best) taken.add(best.id);
      return best?.id ?? null;
    });
    const b = elenco.b.map((id) => (id && taken.has(id) ? null : id));
    const f = elenco.f.map((id) => (id && taken.has(id) ? null : id));
    // Quem saiu do campo vai para o primeiro lugar vazio de fora.
    for (const id of elenco.t) {
      if (!id || taken.has(id)) continue;
      const vazio = f.indexOf(null);
      if (vazio >= 0) f[vazio] = id;
      else f.push(id);
    }
    setElenco({ t: rebuilt, b, f });
    const { custom: _desenho, ...semDesenho } = tactic;
    setTactic({ ...semDesenho, formation: name });
    setPego(null);
  }

  function alternar<T extends string>(lista: Array<[T, Chave]>, valor: T): T {
    const indice = lista.findIndex(([chaveDoValor]) => chaveDoValor === valor);
    return (lista[(indice + 1) % lista.length] ?? lista[0])![0];
  }

  const papelDe = (id: string): Papel[] =>
    PAPEIS.filter(({ papel }) =>
      papel === "capitao" ? tactic.captainId === id : tactic.takers[papel] === id,
    ).map(({ papel }) => papel);

  function marcarPapel(papel: Papel, id: string): void {
    if (papel === "capitao") return setTactic({ ...tactic, captainId: id });
    setTactic({
      ...tactic,
      takers: { ...tactic.takers, [papel]: tactic.takers[papel] === id ? null : id },
    });
  }

  const completo = elenco.t.every((id) => id !== null);
  const opponent = view.opponent;

  const naTela = assinatura(elenco.t, elenco.b, tactic);
  const vagasDaView = view.formations.find((entry) => entry.name === view.tactic.formation)?.slots.length ?? 11;
  const daView = montarElenco(view, vagasDaView);
  const naFila = naTela === assinatura(daView.t, daView.b, view.tactic);

  const confirmar = (): void => {
    enviado.current = naTela;
    onConfirm(
      {
        starters: elenco.t.filter((id): id is string => id !== null),
        bench: elenco.b.filter((id): id is string => id !== null),
        autoSubs: [],
      },
      tactic,
    );
  };

  // Mudança completa vai sozinha para a fila, um instante depois do toque. A
  // mesma escalação não sai duas vezes: se o motor recusou, fica o aviso dele.
  useEffect(() => {
    if (!completo || naFila || busy || enviado.current === naTela) return;
    const relogio = window.setTimeout(confirmar, 200);
    return () => window.clearTimeout(relogio);
  }, [naTela, naFila, completo, busy]);

  return (
    <div className="tatica" ref={raiz} data-segurando={pego ? "" : undefined}>
      <section className="card tatica-card">
        <header className="tatica-topo" data-pego={pego ? "" : undefined}>
          <h2 className="label">{t("tatica.escalacao")}</h2>
          {pego && (
            <FichaDoPego
              t={t}
              jogador={byId.get(idEm(pego) ?? "")}
              vaga={pego.lista === "t" ? (slots[pego.i] ?? null) : null}
              nota={pego.lista === "t" ? notaNaVaga(pego.i) : null}
              papeis={idEm(pego) ? papelDe(idEm(pego) as string) : []}
              aoMarcar={(papel) => {
                const id = idEm(pego);
                if (id) marcarPapel(papel, id);
              }}
              aoSoltar={() => setPego(null)}
            />
          )}
          <div className="tatica-acoes">
            <span className="tatica-forca">
              <span className="label-muted">{t("tatica.forca")}</span>
              <b className="num" style={{ color: strengthColor(forca) }}>{forca}</b>
            </span>
            <button type="button" className="ghost" onClick={() => onAuto(tactic)} disabled={busy}>
              {t("tatica.automatica")}
            </button>
            <button
              type="button"
              className="primary"
              disabled={busy || !completo || naFila}
              onClick={confirmar}
            >
              {naFila ? t("tatica.confirmada") : t("tatica.confirmarCurto")}
            </button>
          </div>
        </header>

        <div className="tatica-corpo">
          <div className="tatica-palco">
            <div className="tatica-coluna-campo">
              <EscalacaoNoCampo
                formacao={personalizada ? CUSTOM_FORMATION : (formation?.name ?? "")}
                slots={slots}
                pontos={personalizada}
                starters={elenco.t.map((id) => id ?? "")}
                porId={byId}
                rendimentoDe={ratingOf}
                aoEscolher={(indice) => tocar({ lista: "t", i: indice })}
                estilo={tactic.style}
                marcacao={tactic.marking}
                classeDa={(indice) => classeDe({ lista: "t", i: indice })}
                marcasDa={(indice) => {
                  const id = elenco.t[indice];
                  const jogador = id ? byId.get(id) : undefined;
                  return jogador ? (
                    <Marcas t={t} jogador={jogador} vaga={slots[indice] ?? null} papeis={papelDe(jogador.id)} />
                  ) : null;
                }}
                rotuloDa={(indice) => {
                  const id = elenco.t[indice];
                  const jogador = id ? byId.get(id) : undefined;
                  return jogador
                    ? t("tatica.rotuloDaFicha", {
                        nome: jogador.name,
                        pos: rotuloDePosicao(slots[indice] ?? jogador.pos),
                        nota: notaNaVaga(indice),
                        condicao: jogador.condition,
                      })
                    : t("tatica.vagaAberta");
                }}
              >
                <div className="tatica-sobre-topo">
                  <label className="tatica-vidro tatica-formacao">
                    <select
                      aria-label={t("tatica.formacao")}
                      value={personalizada ? CUSTOM_FORMATION : tactic.formation}
                      onChange={(e) => changeFormation(e.target.value)}
                    >
                      {personalizada && <option value={CUSTOM_FORMATION}>{t("tatica.personalizada")}</option>}
                      {view.formations.map((entry) => (
                        <option key={entry.name} value={entry.name}>
                          {entry.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="tatica-alternas">
                    <Alterna
                      rotulo={t("tatica.marcacao")}
                      valor={t(MARCACOES.find(([v]) => v === tactic.marking)?.[1] ?? "tatica.marcacaoNormal")}
                      nivel={MARCACOES.findIndex(([v]) => v === tactic.marking)}
                      icone={<IconeMarcacao />}
                      aoTocar={() => setTactic({ ...tactic, marking: alternar(MARCACOES, tactic.marking) })}
                    />
                    <Alterna
                      rotulo={t("tatica.ritmo")}
                      valor={t(RITMOS.find(([v]) => v === tactic.tempo)?.[1] ?? "tatica.ritmoNormal")}
                      nivel={RITMOS.findIndex(([v]) => v === tactic.tempo)}
                      icone={<IconeRitmo />}
                      aoTocar={() => setTactic({ ...tactic, tempo: alternar(RITMOS, tactic.tempo) })}
                    />
                  </div>
                </div>

                <div className="tatica-setores">
                  <Setor rotulo={t("tatica.setorAta")} valor={meus.ata} />
                  <Setor rotulo={t("tatica.setorMei")} valor={meus.mei} />
                  <Setor rotulo={t("tatica.setorDef")} valor={meus.def} />
                </div>

                <div className="tatica-vidro tatica-regua" role="group" aria-label={t("tatica.estilo")}>
                  {ESTILOS.map(([estilo, rotulo]) => (
                    <button
                      key={estilo}
                      type="button"
                      title={t(rotulo)}
                      aria-label={t(rotulo)}
                      aria-pressed={tactic.style === estilo}
                      onClick={() => setTactic({ ...tactic, style: estilo })}
                    >
                      <IconeEstilo estilo={estilo} />
                    </button>
                  ))}
                </div>
                <Legenda t={t} />
              </EscalacaoNoCampo>
            </div>

            <div className="tatica-lado" data-aba={aba}>
              <div className="tatica-abas" role="tablist">
                <button type="button" role="tab" aria-selected={aba === "banco"} onClick={() => setAba("banco")}>
                  {t("tatica.abaBanco")}
                </button>
                {opponent && (
                  <button type="button" role="tab" aria-selected={aba === "adversario"} onClick={() => setAba("adversario")}>
                    <Crest colors={opponent.colors} short={opponent.short} size={16} semSigla />
                    {opponent.name}
                  </button>
                )}
              </div>

              <div className="tatica-grupo tatica-grupo-banco">
                <header>
                  <h3 className="label-muted">{t("tatica.reservas")}</h3>
                  <span className="label-muted num">
                    {elenco.b.filter(Boolean).length}/{VAGAS_NO_BANCO}
                  </span>
                </header>
                <div className="tatica-fichas">
                  {elenco.b.map((id, i) => (
                    <FichaDeFora
                      key={i}
                      t={t}
                      jogador={id ? byId.get(id) : undefined}
                      lugar={{ lista: "b", i }}
                      classe={classeDe({ lista: "b", i })}
                      papeis={id ? papelDe(id) : []}
                      aoTocar={tocar}
                    />
                  ))}
                </div>
              </div>

              <div className="tatica-grupo tatica-grupo-fora">
                <header>
                  <h3 className="label-muted">{t("tatica.foraDosRelacionados")}</h3>
                </header>
                <div className="tatica-fichas">
                  {elenco.f.map((id, i) => (
                    <FichaDeFora
                      key={i}
                      t={t}
                      jogador={id ? byId.get(id) : undefined}
                      lugar={{ lista: "f", i }}
                      classe={classeDe({ lista: "f", i })}
                      papeis={[]}
                      fora
                      aoTocar={tocar}
                    />
                  ))}
                </div>
              </div>

              {opponent && <PainelDoAdversario t={t} adversario={opponent} meus={meus} />}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

/** Selos de capitão e cobrador de um lado, alertas do outro. */
function Marcas({
  t,
  jogador,
  vaga,
  papeis,
}: {
  t: Traduzir;
  jogador: PlayerRow;
  vaga: string | null;
  papeis: Papel[];
}): ReactNode {
  return (
    <>
      {papeis.length > 0 && (
        <span className="tatica-selos">
          {PAPEIS.filter(({ papel }) => papeis.includes(papel)).map(({ papel, letra, classe, rotulo }) => (
            <span key={papel} className={`tatica-selo ${classe}`} title={t(rotulo)}>
              {letra}
            </span>
          ))}
        </span>
      )}
      <span className="tatica-alertas">
        {jogador.injuryRounds > 0 && (
          <span className="tatica-al lesao" title={t("tatica.tituloLesionado", { rodadas: jogador.injuryRounds })}>
            <IconeLesao />
          </span>
        )}
        {jogador.suspensionRounds > 0 && (
          <span className="tatica-al suspenso" title={t("tatica.tituloSuspenso", { rodadas: jogador.suspensionRounds })}>
            {jogador.suspensionRounds}
          </span>
        )}
        {vaga && jogador.pos !== vaga && (
          <span className="tatica-al improv" title={t("tatica.tituloImprovisado", { pos: rotuloDePosicao(jogador.pos) })}>
            <IconeImproviso />
          </span>
        )}
        {cansado(jogador) && (
          <span className="tatica-al cansado" title={t("tatica.tituloCansado", { valor: jogador.condition })}>
            <IconeCansado />
          </span>
        )}
        {pendurado(jogador) && (
          <span className="tatica-al pendurado" title={t("tatica.tituloPendurado", { amarelos: jogador.yellow })}>
            {jogador.yellow}
          </span>
        )}
      </span>
    </>
  );
}

/** Uma ficha do banco ou dos de fora, ou o lugar vazio que ela deixou. */
function FichaDeFora({
  t,
  jogador,
  lugar,
  classe,
  papeis,
  fora = false,
  aoTocar,
}: {
  t: Traduzir;
  jogador: PlayerRow | undefined;
  lugar: Lugar;
  classe: string;
  papeis: Papel[];
  fora?: boolean;
  aoTocar: (lugar: Lugar) => void;
}): ReactNode {
  if (!jogador) {
    return (
      <button
        type="button"
        className={`tatica-ficha vazia ${classe}`}
        data-lista={lugar.lista}
        data-i={lugar.i}
        aria-label={t("tatica.lugarVazio")}
        onClick={() => aoTocar(lugar)}
      >
        <span className="ficha-foto vazio" />
        <span className="tatica-ficha-nome">
          &nbsp;
          <br />
          &nbsp;
        </span>
      </button>
    );
  }
  const partes = jogador.name.trim().split(/\s+/);
  const travado = indisponivel(jogador);
  return (
    <button
      type="button"
      className={`tatica-ficha${fora ? " fora" : ""}${travado ? " travado" : ""} ${classe}`}
      data-lista={lugar.lista}
      data-i={lugar.i}
      aria-label={t("tatica.rotuloDaFicha", {
        nome: jogador.name,
        pos: rotuloDePosicao(jogador.pos),
        nota: jogador.strength,
        condicao: jogador.condition,
      })}
      onClick={() => aoTocar(lugar)}
    >
      <span
        className="ficha-foto"
        style={{ "--cond": jogador.condition } as CSSProperties}
        data-cond={faixaDeCondicao(jogador.condition)}
      >
        <span className="campo-retrato">
          <AvatarDoJogador playerId={jogador.id} nacionalidade={jogador.nationality} rosto />
        </span>
        <span className="tatica-pos" data-setor={jogador.sector}>
          {rotuloDePosicao(jogador.pos)}
        </span>
        <span className="campo-forca" style={{ background: strengthColor(jogador.strength) }}>
          {jogador.strength}
        </span>
        <Marcas t={t} jogador={jogador} vaga={null} papeis={papeis} />
      </span>
      {/* Nome em cima, sobrenome embaixo: quebra de linha em vez de corte. */}
      <span className="tatica-ficha-nome">
        {partes.length > 1 ? (
          <>
            {partes[0]}
            <br />
            {partes.slice(1).join(" ")}
          </>
        ) : (
          jogador.name
        )}
      </span>
    </button>
  );
}

/** A ficha do jogador pego, no cabeçalho, com capitão e cobradores. */
function FichaDoPego({
  t,
  jogador,
  vaga,
  nota,
  papeis,
  aoMarcar,
  aoSoltar,
}: {
  t: Traduzir;
  jogador: PlayerRow | undefined;
  vaga: string | null;
  nota: number | null;
  papeis: Papel[];
  aoMarcar: (papel: Papel) => void;
  aoSoltar: () => void;
}): ReactNode {
  if (!jogador) return null;
  const titular = vaga !== null;
  const improvisado = titular && jogador.pos !== vaga;
  return (
    <div className="tatica-hud">
      <span
        className="ficha-foto"
        style={{ "--cond": jogador.condition } as CSSProperties}
        data-cond={faixaDeCondicao(jogador.condition)}
      >
        <span className="campo-retrato">
          <AvatarDoJogador playerId={jogador.id} nacionalidade={jogador.nationality} rosto />
        </span>
      </span>
      <div className="tatica-hud-quem">
        <b>{jogador.name}</b>
        <span>
          {t("tatica.posicaoEIdade", {
            pos: improvisado
              ? t("tatica.naturalEm", { natural: rotuloDePosicao(jogador.pos), vaga: rotuloDePosicao(vaga ?? "") })
              : rotuloDePosicao(jogador.pos),
            idade: jogador.age,
          })}
        </span>
      </div>
      <div className="tatica-hud-dados">
        <div>
          <small>{titular ? t("tatica.naVaga") : t("tatica.forca")}</small>
          <b className={improvisado ? "ruim" : undefined}>
            {titular ? nota : jogador.strength}
            {improvisado && <i>/{jogador.strength}</i>}
          </b>
        </div>
        <div>
          <small>{t("tatica.condicaoCurta")}</small>
          <b className={jogador.condition < 75 ? "ruim" : "bom"}>{jogador.condition}%</b>
        </div>
        <div>
          <small>{t("tatica.moral")}</small>
          <b>
            <MoraleFace value={jogador.morale} size={15} />
          </b>
        </div>
        <div>
          <small>{t("tatica.media")}</small>
          <b>{jogador.rating > 0 ? jogador.rating.toFixed(1) : "-"}</b>
        </div>
        <div>
          <small>{t("tatica.golsAssist")}</small>
          <b>
            {jogador.goals}/{jogador.assists}
          </b>
        </div>
      </div>
      <div className="tatica-hud-papeis">
        {titular &&
          PAPEIS.map(({ papel, letra, rotulo, classe }) => (
            <button
              key={papel}
              type="button"
              className="tatica-papel"
              aria-pressed={papeis.includes(papel)}
              title={t(rotulo)}
              aria-label={t(rotulo)}
              onClick={() => aoMarcar(papel)}
            >
              <span className={`tatica-selo ${classe}`}>{letra}</span>
            </button>
          ))}
      </div>
      <button type="button" className="tatica-soltar" aria-label={t("tatica.soltar")} onClick={aoSoltar}>
        ✕
      </button>
    </div>
  );
}

/** O adversário do próximo jogo, para montar a escalação olhando para ele. */
function PainelDoAdversario({
  t,
  adversario,
  meus,
}: {
  t: Traduzir;
  adversario: OpponentView;
  meus: { ata: number; mei: number; def: number };
}): ReactNode {
  const deles = setores(adversario.formation, adversario.slots, (i) => adversario.starters[i]?.rating ?? 0);
  const linhas = faixasDaEscalacao(adversario.formation, adversario.slots);
  const duelos: Array<[Chave, number, number]> = [
    ["tatica.dueloAtaque", meus.ata, deles.def],
    ["tatica.dueloMeio", meus.mei, deles.mei],
    ["tatica.dueloDefesa", meus.def, deles.ata],
  ];
  const estilo = ESTILOS.find(([v]) => v === adversario.style);
  const marcacao = MARCACOES.find(([v]) => v === adversario.marking);
  const ritmo = RITMOS.find(([v]) => v === adversario.tempo);

  return (
    <section className="tatica-adversario" aria-label={t("tatica.adversario")}>
      <header className="tatica-adv-topo">
        <Crest colors={adversario.colors} short={adversario.short} size={30} />
        <div>
          <b>{adversario.name}</b>
          {adversario.hasTable && (
            <span className="label-muted">
              {t("tatica.posicaoEPontos", { posicao: adversario.position, pontos: adversario.points })}
            </span>
          )}
        </div>
        <span className="tatica-adv-forma">
          <FormChips form={adversario.form} />
        </span>
      </header>

      <div className="tatica-adv-corpo">
        {/*
          O ataque deles vem de cima para baixo, na direção do seu gol: é o
          campo do seu time visto de frente para o adversário.
        */}
        <div className="tatica-mini-campo">
          {linhas.map((linha) => {
            const y = 8 + (1 - linha.v) * 84;
            return linha.vagas.map((vaga, ordem) => {
              const x = linha.vagas.length === 1 ? 50 : 14 + (72 * ordem) / (linha.vagas.length - 1);
              const jogador = adversario.starters[vaga.indice];
              return (
                <span key={vaga.indice} className="tatica-adv-jog" style={{ left: `${String(x)}%`, top: `${String(y)}%` }}>
                  <span
                    className="tatica-ponto"
                    style={{ background: adversario.colors[0], borderColor: adversario.colors[1] }}
                  >
                    {jogador?.rating ?? ""}
                  </span>
                  <span className="tatica-ponto-nome">{jogador ? nomeCurto(jogador.name) : ""}</span>
                </span>
              );
            });
          })}
          <span className="tatica-mini-form">
            {adversario.formation === CUSTOM_FORMATION ? t("tatica.personalizada") : adversario.formation}
          </span>
        </div>

        <div className="tatica-adv-dados">
          <div className="tatica-tendencia">
            {estilo && (
              <span className="tatica-tag">
                <IconeEstilo estilo={adversario.style} />
                {t(estilo[1])}
              </span>
            )}
            {marcacao && (
              <span className="tatica-tag">
                <IconeMarcacao />
                {t(marcacao[1])}
              </span>
            )}
            {ritmo && (
              <span className="tatica-tag">
                <IconeRitmo />
                {t(ritmo[1])}
              </span>
            )}
          </div>
          <div className="tatica-duelos">
            {duelos.map(([rotulo, nosso, deles]) => {
              const diferenca = nosso - deles;
              return (
                <div key={rotulo} className="tatica-duelo">
                  <small>{t(rotulo)}</small>
                  <b className="num">{nosso}</b>
                  <span className="x">x</span>
                  <b className="num">{deles}</b>
                  <span className={`tatica-dif ${diferenca > 0 ? "mais" : diferenca < 0 ? "menos" : "igual"}`}>
                    {diferenca > 0 ? "+" : ""}
                    {diferenca}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="tatica-adv-listas">
        {adversario.highlights.length > 0 && (
          <div>
            <h4 className="label-muted">{t("tatica.destaques")}</h4>
            <ul>
              {adversario.highlights.map((jogador) => (
                <li key={jogador.name}>
                  <span className="tatica-pos-linha" data-pos={jogador.pos}>
                    {rotuloDePosicao(jogador.pos)}
                  </span>
                  <span className="n">{jogador.name}</span>
                  <span className="info">
                    {jogador.kind === "gols"
                      ? t("tatica.destaqueGols", { valor: jogador.value })
                      : jogador.kind === "assistencias"
                        ? t("tatica.destaqueAssist", { valor: jogador.value })
                        : t("tatica.destaqueCapitao")}
                  </span>
                  <span className="r num" style={{ color: strengthColor(jogador.rating) }}>
                    {jogador.rating}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {adversario.absences.length > 0 && (
          <div>
            <h4 className="label-muted">{t("tatica.desfalques")}</h4>
            <ul>
              {adversario.absences.map((jogador) => (
                <li key={`${jogador.name}-${jogador.kind}`}>
                  <span className="tatica-pos-linha" data-pos={jogador.pos}>
                    {rotuloDePosicao(jogador.pos)}
                  </span>
                  <span className="n">{jogador.name}</span>
                  {jogador.kind === "lesao" ? (
                    <span className="tatica-al lesao" title={t("tatica.tituloLesionado", { rodadas: jogador.count })}>
                      <IconeLesao />
                    </span>
                  ) : (
                    <span className={`tatica-al ${jogador.kind}`}>{jogador.count}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * A legenda, numa dica flutuante no canto do gramado.
 *
 * Já foi uma sanfona embaixo do campo, e abrir a sanfona tirava altura do
 * campo: ele encolhia, a coluna dele estreitava e as reservas ao lado ficavam
 * desalinhadas. Por cima de tudo, ela não ocupa lugar nenhum. Abre ao passar o
 * mouse, ao focar pelo teclado e ao tocar, que é o que funciona no celular.
 */
function Legenda({ t }: { t: Traduzir }): ReactNode {
  const [aberta, setAberta] = useState(false);
  return (
    <div
      className="tatica-legenda"
      data-aberta={aberta ? "" : undefined}
      onMouseLeave={() => setAberta(false)}
    >
      <button
        type="button"
        className="tatica-vidro tatica-legenda-botao"
        aria-expanded={aberta}
        aria-label={t("tatica.legenda")}
        title={t("tatica.legenda")}
        onClick={() => setAberta((antes) => !antes)}
        onBlur={() => setAberta(false)}
      >
        ?
      </button>
      <div className="tatica-vidro tatica-legenda-itens" role="tooltip">
        <span>
          <span className="tatica-mini-anel" />
          {t("tatica.legCondicao")}
        </span>
        <span>
          <span className="tatica-al improv">
            <IconeImproviso />
          </span>
          {t("tatica.legImprovisado")}
        </span>
        <span>
          <span className="tatica-al cansado">
            <IconeCansado />
          </span>
          {t("tatica.legCansado")}
        </span>
        <span>
          <span className="tatica-al pendurado">2</span>
          {t("tatica.legPendurado")}
        </span>
        <span>
          <span className="tatica-al lesao">
            <IconeLesao />
          </span>
          {t("tatica.legLesionado")}
        </span>
        <span>
          <span className="tatica-al suspenso">1</span>
          {t("tatica.legSuspenso")}
        </span>
        {PAPEIS.map(({ papel, letra, rotulo, classe }) => (
          <span key={papel}>
            <span className={`tatica-selo ${classe}`}>{letra}</span>
            {t(rotulo)}
          </span>
        ))}
      </div>
    </div>
  );
}

function Alterna({
  rotulo,
  valor,
  nivel,
  icone,
  aoTocar,
}: {
  rotulo: string;
  valor: string;
  nivel: number;
  icone: ReactNode;
  aoTocar: () => void;
}): ReactNode {
  return (
    <button type="button" className="tatica-vidro tatica-alterna" onClick={aoTocar} aria-label={`${rotulo}: ${valor}`}>
      {icone}
      <span>
        <small>{rotulo}</small>
        <b>{valor}</b>
      </span>
      <span className="tatica-pontos" aria-hidden="true">
        {[0, 1, 2].map((ponto) => (
          <span key={ponto} data-on={ponto <= nivel ? "" : undefined} />
        ))}
      </span>
    </button>
  );
}

function Setor({ rotulo, valor }: { rotulo: string; valor: number }): ReactNode {
  return (
    <div className="tatica-vidro tatica-setor">
      <small>{rotulo}</small>
      <b className="num">{valor}</b>
    </div>
  );
}

function IconeEstilo({ estilo }: { estilo: TacticInput["style"] }): ReactNode {
  const caminho = estilo === "ofensivo" ? "M3 9l5-5 5 5M3 13l5-5 5 5" : estilo === "defensivo" ? "M3 3l5 5 5-5M3 7l5 5 5-5" : "M3 6h10M3 10h10";
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={caminho} />
    </svg>
  );
}

function IconeMarcacao(): ReactNode {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="5" cy="8" r="2.5" />
      <circle cx="11" cy="8" r="2.5" />
    </svg>
  );
}

function IconeRitmo(): ReactNode {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M2 8h8M6 4l4 4-4 4M11 4l3 4-3 4" />
    </svg>
  );
}

function IconeImproviso(): ReactNode {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M2 4h7l-2-2M10 8H3l2 2" />
    </svg>
  );
}

function IconeCansado(): ReactNode {
  return (
    <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
      <path d="M7 1L3 7h3l-1 4 4-6H6z" />
    </svg>
  );
}

function IconeLesao(): ReactNode {
  return (
    <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
      <path d="M5 2h2v3h3v2H7v3H5V7H2V5h3z" />
    </svg>
  );
}
